import { Controlbutton, ControlsContainer, ResultH1, ResultH25, ResultH28, SubComponent, UVInfoBlock, UVInfoList, UVInfoListItem, UVInfoSections, UVInfoText, UVInfoTitle, UVPictureCanvas, UVPictureSkeleton, UVTextContainer, VideoHealth, WhiteButton } from '../../styles/newResultsStyle';
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';
import minus from '../../assets/icons/minus.svg';
import plus from '../../assets/icons/plus.svg';
import { CliniqueState, ResultStep } from '../../type';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import fatigueVideo from "../../assets/videos/Discoloration.mp4"
import wrinklesVideo from "../../assets/videos/Wrinkles.mp4"
import ConcernImage from '../ConcernImage';

interface UVDamagesI {
	setResultStep: Function;
	resultStep: ResultStep;
}

export const UVDamages = (props: UVDamagesI) => {
	const { demkyInvisibleDamage } = useSelector((state: CliniqueState) => state.session);
	const { t, i18n } = useTranslation();
	const isActive = props.resultStep === ResultStep.uvProtection;
	const hasDemkyInvisibleDamage = Boolean(demkyInvisibleDamage?.detectedPixels?.length);

	return (
		<SubComponent
			style={{
				backgroundColor: 'white',
				justifyContent: 'initial',
				height: isActive ? 'auto' : '200svh',
				maxHeight: isActive ? 'none' : '200svh',
				overflow: isActive ? 'visible' : 'hidden',
			}}
			$height={isActive ? 'auto' : '0px'}>
			<div style={{ position: 'relative', maxHeight: window.innerWidth > 768 ? '800px' : 'initial', overflow: 'hidden', minHeight: "calc(100vw / 1.34)" }}>
				<TransformWrapper initialScale={1} initialPositionX={0} initialPositionY={0}>
					{({ zoomIn, zoomOut, resetTransform, ...rest }) => (
						<>
							<Controls />
							<TransformComponent>
								{hasDemkyInvisibleDamage ? <ConcernImage as={UVPictureCanvas} image='' concern='invisible_damages' /> : <UVPictureSkeleton />}
							</TransformComponent>
						</>
					)}
				</TransformWrapper>
			</div>

			<UVTextContainer>
				<div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '12px' }}>
					<ResultH1 style={{ textAlign: 'start' }}>{t('your-uv-damage')}</ResultH1>

					<ResultH25>{t('uvdamage-our-tech')}</ResultH25>
				</div>

				<div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '16px', alignItems: 'start' }}>
					<UVInfoSections>

						<UVInfoBlock>
							<UVInfoTitle>{t('uv-damage-what-it-is-title', "What it is:")}</UVInfoTitle>
							<UVInfoText>{t('uv-damage-what-it-is-text-1', "The shorter UVB rays work more quickly, affect the outer layer of skin (epidermis), and are most responsible for burning. UVB radiation has high energy and damages primarily the epidermal layer. With longer UVA rays, tanning and burning occur more slowly. UVA rays penetrate more deeply into the skin (dermis), where they are responsible for the breakdown of collagen and elastin. UVA and UVB radiation can lead to skin cancer. It is important to protect against both daily. ")}</UVInfoText>
							<UVInfoText>{t('uv-damage-what-it-is-text-2')}</UVInfoText>
						</UVInfoBlock>

						<UVInfoBlock>
							<UVInfoTitle>{t('uv-damage-why-it-happens-title', "Why it happens:")}</UVInfoTitle>
							<UVInfoText>{t('uv-damage-why-it-happens-text-1', "Damage from sun exposure is cumulative, which means that harmful effects can build up with each exposure, whether a sunburn occurs or not. Short, daily exposures like walking to your car or sitting by a window contribute to this accumulation. Too much sun exposure can also lead to visible signs of premature aging of the skin in the form of lines, wrinkles, and discoloration. ")}</UVInfoText>
							<UVInfoText>{t('uv-damage-why-it-happens-text-2', "Even visible light, especially blue light (440-500nm), and infrared radiation (IR) can damage the look of skin by producing reactive oxygen species (ROS), a form of free radicals.")}</UVInfoText>
						</UVInfoBlock>

						<div>
							<VideoHealth src={fatigueVideo} playsInline loop autoPlay muted />
							<span style={{ opacity: 0.8 }}>{t("uneven-skin-tone-dark-spots", "Hyperpigmentation")}</span>
						</div>

						<div>
							<VideoHealth src={wrinklesVideo} playsInline loop autoPlay muted />
							<span style={{ opacity: 0.8 }}>{t("lines-wrinkles", "lines and wrinkles")}</span>
						</div>

						<UVInfoBlock style={{ backgroundColor: "#FAFAFA", padding: "24px" }}>
							<UVInfoTitle>{t('uv-damage-did-you-know-title', "Did you know...")}</UVInfoTitle>
							<UVInfoText>{t('uv-damage-did-you-know-text', "Our guiding dermatologists share that sun exposure triggers a cascade of damage that continues even after you are out of direct sunlight. ")}</UVInfoText>
						</UVInfoBlock>

						<UVInfoBlock>
							<UVInfoTitle>{t('uv-damage-how-clinique-helps-title', "How Clinique helps:")}</UVInfoTitle>
							<UVInfoList>
								<UVInfoListItem>{t('uv-damage-how-clinique-helps-sunscreen', "Wear sunscreen daily. Sunscreens are ingredients that can absorb, scatter, or reflect UV rays. ")}</UVInfoListItem>
								<UVInfoListItem>{t('uv-damage-how-clinique-helps-antioxidants', "Pair sunscreen with antioxidants. Antioxidants help neutralize free radicals caused by environmental aggressors, including UV exposure.")}</UVInfoListItem>
								<UVInfoListItem>{t('uv-damage-how-clinique-helps-repair-barrier', "Repair skins barrier. It serves as a protective shield, keeping moisture in and harmful substances (like irritants out. ")}</UVInfoListItem>
								{i18n.language === "EnUs" && <UVInfoListItem>{t('uv-damage-how-clinique-helps-recommanded-by-dermatologist')}</UVInfoListItem>}
							</UVInfoList>
						</UVInfoBlock>
					</UVInfoSections>

					<WhiteButton onClick={() => {
						props.setResultStep(ResultStep.reco)
						setTimeout(() => {
							window.scrollTo({ top: 700, behavior: "smooth" })
						}, 500);
					}}>
						<ResultH28>{t('uvdamage-seeSPF')}</ResultH28>
					</WhiteButton>
				</div>
			</UVTextContainer>
		</SubComponent>
	);
};

export const Controls = () => {
	const { zoomIn, zoomOut } = useControls();

	return (
		<ControlsContainer>
			<Controlbutton onClick={() => zoomIn()}>
				<img alt='zoom in' src={plus} />
			</Controlbutton>
			<Controlbutton onClick={() => zoomOut()}>
				<img alt='zoom out' src={minus} />
			</Controlbutton>
		</ControlsContainer>
	);
};
